/**
 * Store Info Component for Puck Editor
 * 
 * Informasi toko - alamat, jam buka, kontak
 */

import type { ReactNode } from 'react'
import type { ComponentConfig } from '@measured/puck'

export interface StoreInfoProps {
  storeName: string
  address: string
  phone: string
  email: string
  openHours: string
  showMap: boolean
  mapEmbed: string
  variant: 'card' | 'inline' | 'full'
}

// Icons
const LocationIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)

const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
)

const EmailIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
)

const ClockIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

// Validate map embed URL to prevent XSS attacks
const isValidMapUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:') return false
    
    // Validate Google Maps domains
    if (parsed.hostname === 'www.google.com' || 
        parsed.hostname === 'maps.google.com' ||
        parsed.hostname.endsWith('.google.com')) {
      // Additional check: must be a proper subdomain, not just ending with .google.com
      const parts = parsed.hostname.split('.')
      return parts[parts.length - 2] === 'google' && parts[parts.length - 1] === 'com'
    }
    
    // Validate OpenStreetMap domains
    if (parsed.hostname === 'openstreetmap.org' ||
        parsed.hostname === 'www.openstreetmap.org' ||
        parsed.hostname.endsWith('.openstreetmap.org')) {
      const parts = parsed.hostname.split('.')
      return parts[parts.length - 2] === 'openstreetmap' && parts[parts.length - 1] === 'org'
    }
    
    return false
  } catch {
    return false
  }
}

export function StoreInfo({
  storeName,
  address,
  phone,
  email,
  openHours,
  showMap,
  mapEmbed,
  variant,
}: StoreInfoProps) {
  const InfoItem = ({
    icon,
    label,
    value,
    href,
  }: {
    icon: ReactNode
    label: string
    value: string
    href?: string
  }) => (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 text-primary-500">{icon}</div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        {href ? (
          <a href={href} className="text-gray-900 hover:text-primary-500">
            {value}
          </a>
        ) : (
          <p className="text-gray-900">{value}</p>
        )}
      </div>
    </div>
  )

  if (variant === 'full') {
    return (
      <div className="bg-gray-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">{storeName}</h3>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            {address && (
              <InfoItem icon={<LocationIcon />} label="Alamat" value={address} />
            )}
            {phone && (
              <InfoItem
                icon={<PhoneIcon />}
                label="Telepon"
                value={phone}
                href={`tel:${phone.replace(/\D/g, '')}`}
              />
            )}
            {email && (
              <InfoItem
                icon={<EmailIcon />}
                label="Email"
                value={email}
                href={`mailto:${email}`}
              />
            )}
            {openHours && (
              <InfoItem icon={<ClockIcon />} label="Jam Buka" value={openHours} />
            )}
          </div>
          {showMap && mapEmbed && isValidMapUrl(mapEmbed) && (
            <div className="aspect-video rounded-lg overflow-hidden">
              <iframe
                src={mapEmbed}
                className="w-full h-full"
                title={`Peta lokasi ${storeName}`}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  if (variant === 'card') {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-lg text-gray-900 mb-4">{storeName}</h3>
        <div className="space-y-3">
          {address && (
            <InfoItem icon={<LocationIcon />} label="Alamat" value={address} />
          )}
          {phone && (
            <InfoItem
              icon={<PhoneIcon />}
              label="Telepon"
              value={phone}
              href={`tel:${phone.replace(/\D/g, '')}`}
            />
          )}
          {email && (
            <InfoItem
              icon={<EmailIcon />}
              label="Email"
              value={email}
              href={`mailto:${email}`}
            />
          )}
          {openHours && (
            <InfoItem icon={<ClockIcon />} label="Jam Buka" value={openHours} />
          )}
        </div>
      </div>
    )
  }

  // Inline variant
  return (
    <div className="flex flex-wrap gap-6">
      {address && (
        <div className="flex items-center gap-2 text-gray-600">
          <LocationIcon />
          <span>{address}</span>
        </div>
      )}
      {phone && (
        <a
          href={`tel:${phone.replace(/\D/g, '')}`}
          className="flex items-center gap-2 text-gray-600 hover:text-primary-500"
        >
          <PhoneIcon />
          <span>{phone}</span>
        </a>
      )}
      {email && (
        <a
          href={`mailto:${email}`}
          className="flex items-center gap-2 text-gray-600 hover:text-primary-500"
        >
          <EmailIcon />
          <span>{email}</span>
        </a>
      )}
      {openHours && (
        <div className="flex items-center gap-2 text-gray-600">
          <ClockIcon />
          <span>{openHours}</span>
        </div>
      )}
    </div>
  )
}

export const StoreInfoConfig: ComponentConfig<StoreInfoProps> = {
  label: 'Info Toko',
  defaultProps: {
    storeName: 'Nama Toko',
    address: 'Jl. Contoh No. 123, Jakarta',
    phone: '081234567890',
    email: 'toko@email.com',
    openHours: 'Senin - Sabtu, 09:00 - 17:00',
    showMap: false,
    mapEmbed: '',
    variant: 'card',
  },
  fields: {
    storeName: {
      type: 'text',
      label: 'Nama Toko',
    },
    address: {
      type: 'textarea',
      label: 'Alamat',
    },
    phone: {
      type: 'text',
      label: 'Telepon',
    },
    email: {
      type: 'text',
      label: 'Email',
    },
    openHours: {
      type: 'text',
      label: 'Jam Buka',
    },
    showMap: {
      type: 'radio',
      label: 'Tampilkan Peta',
      options: [
        { label: 'Ya', value: true },
        { label: 'Tidak', value: false },
      ],
    },
    mapEmbed: {
      type: 'text',
      label: 'URL Google Maps Embed',
    },
    variant: {
      type: 'select',
      label: 'Gaya',
      options: [
        { label: 'Card', value: 'card' },
        { label: 'Inline', value: 'inline' },
        { label: 'Full', value: 'full' },
      ],
    },
  },
  render: StoreInfo,
}
